/**
 *
 * Playback sync middleware
 *
 * Shares the session's playback state over the data channel: the server keeps it, relays it, and replays it to a joining client.
 *
 */
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { diff } from 'deep-object-diff'
import { DC_MESSAGES } from '~/lib/constants'
import socket from '~/lib/socket'
import transitionManager from '~/lib/transitionManager'
import {
	presetLoaded,
	removeLiveProp,
	setFadeDuration,
	setHoldDuration,
	setInstantTransitions,
	setLoop,
	setPlaybackState,
	setPlaying,
	sharedPlayback,
	toggleLiveProp,
} from '~/store/playback'
import { setProjectId } from '~/store/app'
import { selectAutomationOnWire, selectIsConnected, selectIsRemote, selectIsRunner } from '~/store/app/selectors'
import { adoptSessionProject, warmTextures } from '~/store/app/thunks'
import { selectLiveProps, selectPlaylist } from '~/store/playback/selectors'
import {
	armWhenWarm,
	dropPlan,
	reindexPlayback,
	replan,
	resumePlayingTransition,
	startPlayback,
	stopPlayback,
} from '~/store/playback/thunks'
import { setTextures } from '~/store/ui'
import { selectTexturesReady } from '~/store/ui/selectors'

const logger = console.scope('playbackSync')

export const playbackActions = isAnyOf(
	setInstantTransitions,
	setHoldDuration,
	setFadeDuration,
	setLoop,
	setPlaying,
	presetLoaded
)

const playbackSync = createListenerMiddleware()
playbackSync.startListening({
	matcher: playbackActions,
	effect: async (action, { getState, getOriginalState }) => {
		// A cue carries its own origin in the payload; everything else is flagged on meta.
		if (action.meta?.sync || action.payload?.sync) return

		const current = getState().playback
		const original = getOriginalState().playback

		if (presetLoaded.match(action)) {
			const { index, instant, advance } = action.payload
			// Peers resolve the id in their own playlist; `index` stays for clients that predate it.
			const id = selectPlaylist(getState())[index]?.id
			if (!id) return
			if (!current.playing) socket.sendDC(DC_MESSAGES.CUE, { index, id, instant })
			// The run's own step: peers follow the index only, since the runner's live patch already carries the picture.
			else if (advance && selectIsRunner(getState())) socket.sendDC(DC_MESSAGES.CUE, { index, id, run: true })
			return
		}

		const dif = sharedPlayback(diff(original, current))

		if (Object.keys(dif).length > 0) {
			logger.info('Playback change:', { type: action.type, original, current })
			socket.sendDC(DC_MESSAGES.PLAYBACK, dif)
		}
	},
})

// A duration changed — from this client's field or a peer's PLAYBACK_UPDATE. ⚠️ A listener rather than a call at the field: the remote path lands through `setPlaybackState`, and a peer's change reaches the plan only through this matcher.
playbackSync.startListening({
	matcher: isAnyOf(setHoldDuration, setFadeDuration, setPlaybackState),
	effect: async (action, { getState, getOriginalState, dispatch }) => {
		const was = getOriginalState().playback
		const now = getState().playback
		if (was.hold_duration === now.hold_duration && was.fade_duration === now.fade_duration) return
		dispatch(replan())
	},
})

// ⚠️ Keyed on `selectPlaylist`, not on any mutation: the plan names INDICES into that list, and RTK-Query's optimistic patches move them with no mutation action of their own.
playbackSync.startListening({
	predicate: (action, state, original) => selectPlaylist(state) !== selectPlaylist(original),
	effect: async (action, { getState, getOriginalState, dispatch }) => {
		dispatch(reindexPlayback(selectPlaylist(getOriginalState())))
		dispatch(replan())
		// A remote draws no stage.
		if (!isRemote(getState())) dispatch(warmTextures())
	},
})

// Tolerates a store without `app` (unit rigs).
const isRemote = s => !!s.app && selectIsRemote(s)

// C-project: what this client publishes as the session's project — only the server-named runner, only over an open channel. A template goes out flagged, so a remote never keeps listing the project before it.
const projectToPublish = s =>
	s.app && selectIsConnected(s) && selectAutomationOnWire(s) && selectIsRunner(s) && !isRemote(s) && s.app.project_id
		? `${s.app.is_template ? 'template' : 'project'}:${s.app.project_id}`
		: null

// On a project change, and again on each DC open or runner hand-over: the server replays the last one to joiners.
playbackSync.startListening({
	predicate: (action, state, original) => {
		const key = projectToPublish(state)
		return !!key && key !== projectToPublish(original)
	},
	effect: async (action, { getState }) => {
		const s = getState()
		socket.sendDC(DC_MESSAGES.PLAYBACK, {
			project_id: s.app.project_id,
			project_template: !!s.app.is_template,
		})
	},
})

// C-project: what a remote lists (R5).
const projectToAdopt = s =>
	isRemote(s) && s.playback.project_id
		? `${s.playback.project_template ? 'template' : 'project'}:${s.playback.project_id}`
		: null

playbackSync.startListening({
	predicate: (action, state, original) => {
		const key = projectToAdopt(state)
		return !!key && key !== projectToAdopt(original)
	},
	effect: async (action, { getState, dispatch }) => {
		const { project_id, project_template } = getState().playback
		dispatch(adoptSessionProject(project_id, project_template))
	},
})

// A peer's play or stop is a request only the runner carries out, as its own `togglePlayback`: a fade freezes before the stop, or it dies with the run.
playbackSync.startListening({
	actionCreator: setPlaybackState,
	effect: async (action, { getState, getOriginalState, dispatch }) => {
		const was = getOriginalState().playback.playing
		const now = getState().playback.playing
		// Without a server-named runner every desktop counts as one, and acting here would start a second run.
		if (was === now || !selectAutomationOnWire(getState()) || !selectIsRunner(getState())) return
		if (now) {
			dispatch(resumePlayingTransition())
			dispatch(startPlayback())
		} else {
			if (transitionManager.isActive()) transitionManager.pause()
			dispatch(stopPlayback())
		}
	},
})

// A new runner adopting a playing session waits out the warm, then arms.
playbackSync.startListening({
	actionCreator: setTextures,
	effect: async (action, { getState, getOriginalState, dispatch }) => {
		if (selectTexturesReady(getState()) && !selectTexturesReady(getOriginalState())) dispatch(armWhenWarm())
	},
})

// A plan built against the old project's indices means nothing in the new one, and those indices can coincidentally match presets here.
playbackSync.startListening({
	matcher: isAnyOf(setProjectId),
	effect: async (action, { getState, getOriginalState, dispatch }) => {
		if (getOriginalState().app.project_id === getState().app.project_id) return
		dispatch(dropPlan())
	},
})

// An unpin mid-fade, from this desktop or a phone's intent, joins the running tween so the param catches up to the destination preset.
playbackSync.startListening({
	matcher: isAnyOf(toggleLiveProp, removeLiveProp),
	effect: (action, { getState, getOriginalState }) => {
		const was = selectLiveProps(getOriginalState())
		const now = selectLiveProps(getState())
		if (was === now || !transitionManager.isActive()) return
		for (const key of was) if (!now.includes(key)) transitionManager.joinTransition(key)
	},
})

export default playbackSync
